// 色情報取得サービス

import { apiGetRequest } from '@/utils/apiClient';
import { ColorDefinition } from '@/types/ColorType';

// 色情報のキャッシュ
const colorCache: Map<string, ColorDefinition[]> = new Map();
const labelToColorCache: Map<string, Map<string, ColorDefinition>> = new Map();
const negativeLabelCache: Map<string, Set<string>> = new Map();

// 同時多発を防ぐための単一フライト制御
const inflightAllColorsPromises: Map<string, Promise<ColorDefinition[]>> = new Map();

const buildCacheKey = (languageTag?: string) => {
    const normalized = languageTag?.trim().toLowerCase() ?? '';
    return normalized || 'default';
};

const ensureLabelCache = (languageTag?: string) => {
    const key = buildCacheKey(languageTag);
    if (!labelToColorCache.has(key)) {
        labelToColorCache.set(key, new Map());
    }
    return labelToColorCache.get(key)!;
};

const ensureNegativeCache = (languageTag?: string) => {
    const key = buildCacheKey(languageTag);
    if (!negativeLabelCache.has(key)) {
        negativeLabelCache.set(key, new Set());
    }
    return negativeLabelCache.get(key)!;
};

/**
 * 色定義情報を取得（キャッシュ機能付き）
 * @param provider プロバイダー名（google, outlook, all）
 * @returns 色定義情報のリスト
 */
export const getColors = async (
    provider: string = 'all',
    languageTag?: string,
): Promise<ColorDefinition[]> => {
    if (provider !== 'all') {
        // 互換のために 'all' をベースに解決
        const all = await getAllColorsOnce(languageTag);
        if (provider === 'google') return all.filter((c) => c.key.startsWith('g-'));
        if (provider === 'outlook') return all.filter((c) => c.key.startsWith('o-'));
        return all;
    }
    return await getAllColorsOnce(languageTag);
};

const getAllColorsOnce = async (languageTag?: string): Promise<ColorDefinition[]> => {
    const cacheKey = `all|${buildCacheKey(languageTag)}`;
    if (colorCache.has(cacheKey)) {
        return colorCache.get(cacheKey)!;
    }
    if (inflightAllColorsPromises.has(cacheKey)) {
        return inflightAllColorsPromises.get(cacheKey)!;
    }
    const headers = languageTag ? { 'Accept-Language': languageTag } : undefined;
    const promise = (async () => {
        try {
            const response = await apiGetRequest(`/api/colors?provider=all`, headers);
            const colors: ColorDefinition[] = await response.json();
            // キャッシュ保存
            colorCache.set(cacheKey, colors);
            const labelCache = ensureLabelCache(languageTag);
            colors.forEach((color) => {
                labelCache.set(color.label, color);
                if (color.originalLabel) {
                    labelCache.set(color.originalLabel, color);
                }
            });
            return colors;
        } catch (error) {
            console.error('色情報の取得に失敗しました:', error);
            return [];
        } finally {
            inflightAllColorsPromises.delete(cacheKey);
        }
    })();
    inflightAllColorsPromises.set(cacheKey, promise);
    return promise;
};

/**
 * Google色のみを取得
 */
export const getGoogleColors = async (languageTag?: string): Promise<ColorDefinition[]> => {
    return await getColors('google', languageTag);
};

/**
 * Outlook色のみを取得
 */
export const getOutlookColors = async (languageTag?: string): Promise<ColorDefinition[]> => {
    return await getColors('outlook', languageTag);
};

/**
 * ラベルから色情報を取得
 * @param label 色ラベル
 * @returns 色定義情報（見つからない場合はundefined）
 */
export const getColorByLabel = async (
    label: string,
    languageTag?: string,
): Promise<ColorDefinition | undefined> => {
    const labelCache = ensureLabelCache(languageTag);
    if (labelCache.has(label)) {
        return labelCache.get(label);
    }
    const negativeCache = ensureNegativeCache(languageTag);
    if (negativeCache.has(label)) {
        return undefined;
    }
    // 全色を一括ロードしてからローカル解決
    await getAllColorsOnce(languageTag);
    const hit = labelCache.get(label);
    if (!hit) {
        negativeCache.add(label);
    }
    return hit;
};

/**
 * 色キーからHEX色コードを取得
 * @param colorKey 色キー（例: "g-1", "o-preset1"）
 * @returns HEX色コード（見つからない場合はundefined）
 */
export const getHexByColorKey = async (
    colorKey: string,
    languageTag?: string,
): Promise<string | undefined> => {
    const allColors = await getColors('all', languageTag);
    const color = allColors.find((c) => c.key === colorKey);
    return color?.hexColor;
};

/**
 * 色ラベルからHEX色コードを取得
 * @param label 色ラベル
 * @returns HEX色コード（見つからない場合はundefined）
 */
export const getHexByLabel = async (
    label: string,
    languageTag?: string,
): Promise<string | undefined> => {
    const color = await getColorByLabel(label, languageTag);
    return color?.hexColor;
};

/**
 * キャッシュをクリア
 */
export const clearColorCache = (): void => {
    colorCache.clear();
    labelToColorCache.clear();
    negativeLabelCache.clear();
    inflightAllColorsPromises.clear();
};
